import {
  createStore,
  compose,
  applyMiddleware,
  combineReducers
} from 'redux';
import { thunk } from 'redux-thunk';
import createSagaMiddleware from 'redux-saga'
import { lazyReducerEnhancer } from 'pwa-helpers/lazy-reducer-enhancer';
import { persistEnhancer } from '@dw/pwa-helpers/redux-persist-enhancer';

import app, { adBlockerReducer } from './redux/app/reducers.js';
import appSaga from './redux/app/saga.js';
import deviceSaga from './redux/device/saga.js';
import deviceReducer from './redux/device/reducers.js';
import boardTranslationsReducer from './redux/board-translations/reducers.js';
import boardTranslationsSaga from './redux/board-translations/saga.js';
import { boardExplorer } from './redux/board-explorer/reducers';
import { fileStore, fileStorePreferredStoreSelection } from './redux/file-store/reducers.js';
import { boardSummary } from './redux/board-summary/reducers.js';
import { signupReducer } from './redux/signup/reducers.js';
import { forgotPassword } from './redux/forgot-password/reducers.js';
import * as board from './redux/board';
import  * as router from './redux/router';
import * as auth from './redux/auth';
import * as clipboard from './redux/clipboard';
import * as zoomOutView from './redux/zoomout-view';
export const sagaMiddleware = createSagaMiddleware();
import signupSaga from './redux/signup/saga.js';
import moduleLoader from './redux/module-loader/saga';
import boardSaga from './redux/board/saga';
import loginSaga from './redux/auth/saga.js';
import featureSaga from './redux/feature/saga.js';
import forgotPasswordSaga from './redux/forgot-password/saga.js';
import accessibleAccountsSaga from './redux/auth/accessible-accounts/saga';
import lastVisitedPageSaga from './redux/router/last-visited-page/saga.js';
import fileStoreSaga from './redux/file-store/saga.js';
import boardSummarySaga from './redux/board-summary/saga.js';
import messagesSaga from './redux/messages/saga.js';
import userSaga from './redux/user/saga.js';
import impersonateTranslateSaga from './redux/impersonate-translate/saga.js';
import * as lastMoveCards from './redux/last-move-cards';
import * as cardTranslate from './redux/card-translate';
import { chatReducer } from './redux/chat/reducers.js';
import { cardDetailsReducer } from './redux/card-details/reducers.js';
import * as timeTracker from './redux/time-tracker';
import { getCookieVal } from './utils.js';

// multiple language reducer and saga
import multipleLanguagesReducer  from './redux/multiple-language/reducers.js';
import multipleLanguageSaga from './redux/multiple-language/saga.js';

// cookie consent
import * as cookieConsent from '@kerika/cookie-consent';

const persistOptions = [{ path: 'board-highlights', sharedBetweenTabs: true }, { path: 'clipboard', sharedBetweenTabs: true }, { path: 'zoomout-view', sharedBetweenTabs: true }, {path: 'last-move-cards', sharedBetweenTabs: true}, {path: 'task', sharedBetweenTabs: true}, {path: 'subscription-v2.messages', sharedBetweenTabs: true}, {path: 'ai-chat.panelWidths', sharedBetweenTabs: true}, {path: 'ai-chat.fabPositions', sharedBetweenTabs: true}, {path: 'ai-chat.draftMessages', sharedBetweenTabs: true}, {path: 'ai-chat.newChatDrafts', sharedBetweenTabs: true}, {path: 'ai-chat.newChatIds', sharedBetweenTabs: true}, {path: 'ai-chat.editMessages', sharedBetweenTabs: true}, {path: 'ai-chat.editDrafts', sharedBetweenTabs: true}, {path: 'ai-chat.completedBoardCreations', sharedBetweenTabs: true}, {path: 'ad-blocker.lastShownDate', sharedBetweenTabs: true}, {path: 'time-tracker.dismissedMoves', sharedBetweenTabs: true}, {path: 'chat.newChatDrafts', sharedBetweenTabs: true}, {path: 'chat.editDrafts', sharedBetweenTabs: true}, {path: 'cardDetails.descriptionDrafts', sharedBetweenTabs: true},
  // No `sharedBetweenTabs`: each tab's impersonation session is independent, and a raw cross-tab
  // rehydration here would update this value without ever calling
  // `firestoreRedux.translation.setLanguage(...)` in the other tab, desyncing its displayed
  // language from its actually-translated content. Still persists per device via localStorage,
  // just not live-synced between simultaneously open tabs.
  {path: 'multiple-language.impersonateLang'},
  // No `sharedBetweenTabs` either, and for the same reason as `impersonateLang`: another tab
  // adopting this would pin a language nobody asked for there. Still persists per device via
  // localStorage, so the reader's usual language is at the top of the menu after a reload.
  {path: 'card-translate.lastLanguage'}];

// Sets up a Chrome extension for time travel debugging.
// See https://github.com/zalmoxisus/redux-devtools-extension for more information.
const devCompose = getCookieVal('redux_devtools_access') && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ?
      window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        shouldHotReload: true
      }) : compose;


// Initializes the Redux store with a lazyReducerEnhancer (so that you can
// lazily add reducers after the store has been created) and redux-thunk (so
// that you can dispatch async actions). See the "Redux and state management"
// section of the wiki for more details:
// https://pwa-starter-kit.polymer-project.org/redux-and-state-management
export const store = createStore(
  state => state,
  devCompose(
    lazyReducerEnhancer(combineReducers),
    applyMiddleware(thunk, sagaMiddleware),
    persistEnhancer(persistOptions)
  )
);

sagaMiddleware.run(appSaga);
sagaMiddleware.run(deviceSaga);
sagaMiddleware.run(moduleLoader);
sagaMiddleware.run(boardTranslationsSaga);
sagaMiddleware.run(boardSaga);
sagaMiddleware.run(accessibleAccountsSaga);
sagaMiddleware.run(lastVisitedPageSaga);
sagaMiddleware.run(loginSaga);
sagaMiddleware.run(featureSaga);
sagaMiddleware.run(fileStoreSaga);
sagaMiddleware.run(boardSummarySaga);
sagaMiddleware.run(signupSaga);
sagaMiddleware.run(forgotPasswordSaga);
sagaMiddleware.run(clipboard.saga.clipboardSaga);
sagaMiddleware.run(messagesSaga);
sagaMiddleware.run(lastMoveCards.saga.lastMoveCardsSaga);
sagaMiddleware.run(multipleLanguageSaga);
sagaMiddleware.run(userSaga);
sagaMiddleware.run(impersonateTranslateSaga);
sagaMiddleware.run(cookieConsent.redux.saga);
sagaMiddleware.run(timeTracker.saga.timeTrackerSaga);
sagaMiddleware.run(cardTranslate.saga.cardTranslateSaga);

// Initially loaded reducers.
store.addReducers({
  app,
  auth: auth.reducers.authReducer,
  router: router.reducers.routerReducer,
  'board-translations': boardTranslationsReducer,
  'device': deviceReducer,
  'login-and-signup': auth.reducers.loginAndSignupReducer,
  board: board.reducers.boardReducer,
  'board-team': board.reducers.boardTeamReducer,
  'board-explorer': boardExplorer,
  'multiple-language': multipleLanguagesReducer,
  'file-store': fileStore,
  'file-store-preferred-store-selection': fileStorePreferredStoreSelection,
  'board-summary': boardSummary,
  'signup': signupReducer,
  'forgot-password': forgotPassword,
  clipboard: clipboard.reducers.clipboardReducer,
  'zoomout-view': zoomOutView.reducers.zoomOutViewReducer,
  'last-move-cards': lastMoveCards.reducers.lastMoveCardsReducer,
  'subscription-v2': (state = {}) => state,
  'board-highlights': (state = {}) => state, // As there is an issue in redux's `combineReducer`,
                                            // when any slice is being persisted in localStorage & loaded lazily when needed, it should be added initially.
  'cookieConsent': cookieConsent.redux.reducer,
  'ai-chat': (state = {}) => state,
  // Persisted drafts, so added initially for the same reason as `board-highlights`; the real reducers
  // rather than placeholders, so logout can clear the drafts before either module has loaded.
  chat: chatReducer,
  cardDetails: cardDetailsReducer,
  'ad-blocker': adBlockerReducer,
  'time-tracker': timeTracker.reducers.timeTrackerReducer,
  'card-translate': cardTranslate.reducers.cardTranslateReducer,
});

// Set the store instance for cookie consent
cookieConsent.setStore(store);